import { Controller } from "@hotwired/stimulus"
import { get } from "@rails/request.js"

// Connects to data-controller="ui--select-entity-modal"
// Modal variant of entity selector with search, table/list results, and confirm/cancel.
// Uses @rails/request.js for API calls (NOT native fetch) per CONEK patterns.
export default class extends Controller {
  static targets = [
    "searchInput",
    "resultsContainer",
    "resultsTable",
    "resultsBody",
    "resultsList",
    "emptyState",
    "selectedCount",
    "selectedCountText",
    "hiddenInputs",
    "spinner",
  ]

  static values = {
    searchUrl: String,
    multiple: { type: Boolean, default: false },
    displayAttribute: { type: String, default: "name" },
    columns: { type: Array, default: [] },
    initial: { type: Array, default: [] },
  }

  connect() {
    this._debounceTimer = null
    this._selectedItems = new Map() // id -> item data
    this._pendingItems = new Map() // Working copy during modal session

    // Load initial values
    if (this.initialValue && this.initialValue.length > 0) {
      for (const id of this.initialValue) {
        this._selectedItems.set(String(id), { id: String(id) })
      }
      this._syncPendingFromSelected()
    }

    this._updateSelectedCount()
  }

  disconnect() {
    if (this._debounceTimer) {
      clearTimeout(this._debounceTimer)
    }
  }

  // --- Actions ---

  search() {
    const query = this.searchInputTarget.value.trim()

    if (this._debounceTimer) {
      clearTimeout(this._debounceTimer)
    }

    if (query.length < 1) {
      this._clearResults()
      return
    }

    this._debounceTimer = setTimeout(async () => {
      await this._performSearch(query)
    }, 300)
  }

  confirm() {
    // Commit pending selections to confirmed
    this._selectedItems = new Map(this._pendingItems)
    this._updateHiddenInputs()

    this.element.dispatchEvent(
      new CustomEvent("select-entity-modal:confirm", {
        bubbles: true,
        detail: { selectedIds: this._getSelectedIds() },
      }),
    )

    this._closeModal()
  }

  cancel() {
    // Revert pending to confirmed state
    this._syncPendingFromSelected()
    this._closeModal()
  }

  // --- Private methods ---

  async _performSearch(query) {
    this._showSpinner()

    try {
      const url = new URL(this.searchUrlValue, window.location.origin)
      url.searchParams.set("q", query)

      const response = await get(url.toString(), {
        credentials: "include",
        responseKind: "json",
      })

      if (response.ok) {
        const data = await response.json
        const items = Array.isArray(data)
          ? data
          : data.data || data.items || data.results || []
        this._renderResults(items)
      } else {
        this._renderResults([])
      }
    } catch {
      this._renderResults([])
    } finally {
      this._hideSpinner()
    }
  }

  _renderResults(items) {
    if (items.length === 0) {
      this._clearResults()
      this._showEmptyState()
      return
    }

    this._hideEmptyState()

    if (this.columnsValue.length > 0 && this.hasResultsBodyTarget) {
      this._renderTableResults(items)
    } else if (this.hasResultsListTarget) {
      this._renderListResults(items)
    }
  }

  _renderTableResults(items) {
    this.resultsBodyTarget.innerHTML = ""

    items.forEach((item) => {
      const id = String(item.id)
      const isSelected = this._pendingItems.has(id)

      const tr = document.createElement("tr")
      tr.className = "cursor-pointer hover:bg-muted/50 transition-colors"
      tr.dataset.entityId = id

      // Checkbox cell
      const checkTd = document.createElement("td")
      checkTd.className = "px-4 py-2.5 w-10"
      const checkbox = document.createElement("input")
      checkbox.type = "checkbox"
      checkbox.className =
        "rounded border-neutral-300 text-primary-600 focus:ring-2 focus:ring-primary-500 h-4 w-4"
      checkbox.checked = isSelected
      checkbox.addEventListener("change", () => {
        this._toggleItem(id, item)
      })
      checkTd.appendChild(checkbox)
      tr.appendChild(checkTd)

      // Data cells
      this.columnsValue.forEach((col) => {
        const key = col.key || col["key"]
        const td = document.createElement("td")
        td.className = "px-4 py-2.5 text-sm text-foreground"
        td.textContent = item[key] || ""
        tr.appendChild(td)
      })

      // Click anywhere on row toggles selection
      tr.addEventListener("click", (e) => {
        if (e.target !== checkbox) {
          checkbox.checked = !checkbox.checked
          this._toggleItem(id, item)
        }
      })

      this.resultsBodyTarget.appendChild(tr)
    })
  }

  _renderListResults(items) {
    this.resultsListTarget.innerHTML = ""

    items.forEach((item) => {
      const id = String(item.id)
      const isSelected = this._pendingItems.has(id)
      const displayText =
        item[this.displayAttributeValue] || item.name || item.id

      const el = document.createElement("div")
      el.className =
        "px-4 py-2.5 text-sm text-foreground cursor-pointer hover:bg-muted/50 transition-colors flex items-center gap-3 border-b border-border last:border-b-0"
      el.dataset.entityId = id

      const checkbox = document.createElement("input")
      checkbox.type = "checkbox"
      checkbox.className =
        "rounded border-neutral-300 text-primary-600 focus:ring-2 focus:ring-primary-500 h-4 w-4 shrink-0"
      checkbox.checked = isSelected
      checkbox.addEventListener("change", () => {
        this._toggleItem(id, item)
      })

      const text = document.createElement("span")
      text.textContent = displayText

      el.appendChild(checkbox)
      el.appendChild(text)

      el.addEventListener("click", (e) => {
        if (e.target !== checkbox) {
          checkbox.checked = !checkbox.checked
          this._toggleItem(id, item)
        }
      })

      this.resultsListTarget.appendChild(el)
    })
  }

  _toggleItem(id, item) {
    if (this._pendingItems.has(id)) {
      this._pendingItems.delete(id)
    } else {
      if (!this.multipleValue) {
        // Single select: clear all others
        this._pendingItems.clear()
        // Uncheck all visible checkboxes
        this._uncheckAllVisibleCheckboxes()
      }
      this._pendingItems.set(id, item)
    }

    this._updateSelectedCount()
  }

  _uncheckAllVisibleCheckboxes() {
    const container = this.hasResultsBodyTarget
      ? this.resultsBodyTarget
      : this.hasResultsListTarget
        ? this.resultsListTarget
        : null
    if (!container) return

    container.querySelectorAll("input[type='checkbox']").forEach((cb) => {
      cb.checked = false
    })
  }

  _clearResults() {
    if (this.hasResultsBodyTarget) {
      this.resultsBodyTarget.innerHTML = ""
    }
    if (this.hasResultsListTarget) {
      this.resultsListTarget.innerHTML = ""
    }
    this._hideEmptyState()
  }

  _updateSelectedCount() {
    if (!this.hasSelectedCountTextTarget) return

    const count = this._pendingItems.size
    const label =
      count === 1
        ? `1 ${this.element.dataset.labelItemSelected || "item selected"}`
        : `${count} ${this.element.dataset.labelItemsSelected || "items selected"}`
    this.selectedCountTextTarget.textContent = label
  }

  _updateHiddenInputs() {
    if (!this.hasHiddenInputsTarget) return

    this.hiddenInputsTarget.innerHTML = ""

    // Determine input name from existing hidden inputs or fallback
    const existingInput = this.element.querySelector(
      "input[type='hidden'][name]",
    )
    const inputName = existingInput
      ? existingInput.name
      : this.multipleValue
        ? "selected_ids[]"
        : "selected_id"

    if (this._selectedItems.size === 0) {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = inputName
      input.value = ""
      this.hiddenInputsTarget.appendChild(input)
      return
    }

    this._selectedItems.forEach((_item, id) => {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = inputName
      input.value = id
      this.hiddenInputsTarget.appendChild(input)
    })
  }

  _syncPendingFromSelected() {
    this._pendingItems = new Map(this._selectedItems)
    this._updateSelectedCount()
  }

  _showEmptyState() {
    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.classList.remove("hidden")
    }
    if (this.hasResultsContainerTarget) {
      this.resultsContainerTarget.classList.add("hidden")
    }
  }

  _hideEmptyState() {
    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.classList.add("hidden")
    }
    if (this.hasResultsContainerTarget) {
      this.resultsContainerTarget.classList.remove("hidden")
    }
  }

  _showSpinner() {
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.remove("hidden")
    }
  }

  _hideSpinner() {
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.add("hidden")
    }
  }

  _closeModal() {
    // Find the modal dialog and trigger its close
    const dialog = this.element.querySelector("dialog")
    if (dialog && dialog.open) {
      // Use the modal controller's close method if available
      const modalController =
        this.application.getControllerForElementAndIdentifier(
          dialog,
          "ui--modal",
        )
      if (modalController) {
        modalController.close()
      } else {
        dialog.close()
      }
    }

    // Clear search
    if (this.hasSearchInputTarget) {
      this.searchInputTarget.value = ""
    }
    this._clearResults()
  }

  _getSelectedIds() {
    return Array.from(this._selectedItems.keys())
  }
}
